/**
 * Dice Registration List - Shows all registered dice for the device
 * Displays status, allows deletion, and shows training progress
 */

import { DiceRegistration, RegistrationStatus, ModelStatus } from '@dice-capture/shared/types/dice-registry';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useDiceRegistryStore } from '@/stores/diceRegistryStore';

/**
 * Get badge variant based on registration status
 */
function getStatusVariant(status: RegistrationStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'ready':
      return 'default';
    case 'training':
      return 'secondary';
    case 'uploading':
      return 'outline';
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
}

/**
 * Get human-readable status text
 */
function getStatusText(status: RegistrationStatus, modelStatus?: ModelStatus): string {
  switch (status) {
    case 'ready':
      return modelStatus === 'RUNNING' ? 'Running' : 'Ready';
    case 'training':
      return 'Training...';
    case 'uploading':
      return 'Uploading';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

/**
 * Single registration card
 */
interface RegistrationCardProps {
  registration: DiceRegistration;
  onDelete: () => void;
  onEdit: () => void;
  isDeleting: boolean;
}

function RegistrationCard({ registration, onDelete, onEdit, isDeleting }: RegistrationCardProps) {
  const { diceType, diceName, status, modelStatus, trainedAt, facesUploaded, totalFaces } =
    registration;

  const displayName = diceName || diceType.toUpperCase();

  return (
    <Card className="relative overflow-hidden">
      {/* Status indicator stripe */}
      <div
        className={cn(
          'absolute top-0 left-0 w-1 h-full',
          status === 'ready' && 'bg-green-500',
          status === 'training' && 'bg-yellow-500',
          status === 'uploading' && 'bg-blue-500',
          status === 'failed' && 'bg-red-500'
        )}
      />

      <CardHeader className="pb-2 pl-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{displayName}</CardTitle>
            <CardDescription>{diceType.toUpperCase()}</CardDescription>
          </div>
          <Badge variant={getStatusVariant(status)}>
            {getStatusText(status, modelStatus)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pl-4">
        <div className="text-sm text-muted-foreground space-y-1">
          {status === 'uploading' ? (
            <p>
              Progress: {facesUploaded}/{totalFaces} faces
            </p>
          ) : facesUploaded > 0 && (
            <p>
              {facesUploaded}/{totalFaces} faces captured
            </p>
          )}

          {status === 'training' && (
            <p>Training in progress. This may take 2-4 hours.</p>
          )}

          {status === 'ready' && trainedAt && (
            <p>
              Trained: {new Date(trainedAt).toLocaleDateString()}
            </p>
          )}

          {status === 'ready' && modelStatus === 'RUNNING' && (
            <p className="text-green-500">Model is running</p>
          )}

          {status === 'failed' && (
            <p className="text-destructive">Training failed. Please try again.</p>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          {/* TICKET-088: Edit button - shown for all statuses */}
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            disabled={isDeleting}
          >
            {status === 'uploading' ? 'Continue' : 'Edit'}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * List of all registrations
 */
export function DiceRegistrationList() {
  const { registrations, fetchRegistrations, deleteRegistration, isLoading, deletingRegistrationId, error } =
    useDiceRegistryStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  /**
   * Navigate to edit mode for a registration (TICKET-088)
   */
  const handleEdit = (deviceId: string, diceType: string) => {
    navigate(`/register?edit=true&diceType=${diceType}&deviceId=${deviceId}`);
  };

  if (isLoading && registrations.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Loading registrations...
      </div>
    );
  }

  if (registrations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            No registered dice yet.
            <br />
            Register your first custom dice to get started!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Your Registered Dice</h2>

      {error && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}

      <div className="space-y-3">
        {registrations.map((registration) => (
          <RegistrationCard
            key={registration.registrationId}
            registration={registration}
            onDelete={() => deleteRegistration(registration.registrationId)}
            onEdit={() => handleEdit(registration.deviceId, registration.diceType)}
            isDeleting={deletingRegistrationId === registration.registrationId}
          />
        ))}
      </div>
    </div>
  );
}

export default DiceRegistrationList;
